import { useEffect, useState } from 'react';
import { useChatDocumentViewer } from '@/hooks/useChatDocumentViewer';
import type { McGrawHillFigureBlockPayload } from '@/types/chatBlocks';
import { splitSourceTitle } from './mcgrawHillCitations';
import { fetchMcGrawHillFigureUrl } from './mcgrawHillService';

/** The figure payload, or null when it is not a complete one. */
export const parseMcGrawHillFigurePayload = (
  payload: Record<string, unknown>
): McGrawHillFigureBlockPayload | null => {
  const { documentId, page, label, title, caption, figureId } = payload;
  if (
    typeof documentId !== 'string' ||
    !documentId ||
    typeof page !== 'number' ||
    !Number.isSafeInteger(page) ||
    page < 1 ||
    typeof label !== 'string' ||
    typeof title !== 'string' ||
    typeof caption !== 'string'
  ) {
    return null;
  }

  return {
    documentId,
    page,
    label,
    title,
    caption,
    figureId: typeof figureId === 'string' && figureId ? figureId : null,
  };
};

/** The figure's image as an object URL, once loaded; revoked on change. */
const useFigureImage = (figureId: string | null): string | null => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!figureId) return undefined;
    let url: string | null = null;
    let isCurrent = true;

    fetchMcGrawHillFigureUrl(figureId)
      .then((created) => {
        if (isCurrent) {
          url = created;
          setSrc(created);
        } else {
          URL.revokeObjectURL(created);
        }
      })
      // The placeholder stays: the answer still reads without the image.
      .catch(() => undefined);

    return () => {
      isCurrent = false;
      setSrc(null);
      if (url) URL.revokeObjectURL(url);
    };
  }, [figureId]);

  return src;
};

/**
 * mcgraw-hill: a reference document's figure inside an answer: the label and
 * the document, the figure, its caption, and "Open chapter" to its page (the
 * label highlighted there).
 */
const McGrawHillFigureCard = ({
  payload,
}: {
  payload: McGrawHillFigureBlockPayload;
}) => {
  const { open } = useChatDocumentViewer();
  const { book, rest } = splitSourceTitle(payload.title);
  const imageSrc = useFigureImage(payload.figureId ?? null);

  return (
    <figure className="mcgraw-hill-figure">
      <div className="mcgraw-hill-figure__header">
        <span className="mcgraw-hill-sources__mark" aria-hidden="true" />
        <span className="mcgraw-hill-figure__title">
          <strong>{payload.label}</strong>
          {' · '}
          {book}
          {rest}
        </span>
        <button
          type="button"
          className="mcgraw-hill-figure__open"
          onClick={() =>
            open(
              { kind: 'chatDocument', documentId: payload.documentId },
              { page: payload.page, highlightText: payload.label }
            )
          }
        >
          Open chapter
        </button>
      </div>
      {imageSrc ? (
        <img
          className="mcgraw-hill-figure__image"
          src={imageSrc}
          alt={`${payload.label}. ${payload.caption}`}
        />
      ) : (
        <div className="mcgraw-hill-figure__placeholder">
          {payload.label} image
        </div>
      )}
      <figcaption className="mcgraw-hill-figure__caption">
        {payload.caption}
      </figcaption>
    </figure>
  );
};

export default McGrawHillFigureCard;
